/**
 * card #523 — Dynamische, doc_type-getriebene Kanban-Filter-Tabs.
 *
 * Ersetzt das FIXE Enum (`Alle / Stunden / Diäten / Unklar / Konflikt`), das
 * neue Doc-Typen (Zulagenblatt, Fahrtenbuch) versteckte und nicht filterbar
 * machte. Die Filter-Menge leitet sich jetzt aus den Upload-Optionen ab:
 *
 *     Tabs = {Alle} ∪ docTypes(clientId)
 *                   ∪ {distinct doc_types in cards}
 *                   ∪ {Unklar, Konflikt}
 *
 * - `docTypes(clientId)`: die Upload-Doc-Typen des Mandanten — DIESELBE Quelle,
 *   die der Upload-Dialog nutzt (api.uploads.docTypes). Single Source of Truth,
 *   keine hardcodierte Tab-Menge. Reihenfolge = Mandant-sort_order.
 * - `{distinct doc_types in cards}`: doc_types, die tatsächlich auf Karten
 *   kleben (Server-Aggregat by_doc_type). Ein doc_type, der aus
 *   `mandant_doc_types` ENTFERNT wurde, aber noch auf Karten vorkommt, BEHÄLT
 *   seinen Filter-Tab — markiert `inactive` ("(inaktiv)").
 * - `{Unklar, Konflikt}`: die zwei Sonder-Filter (kind/state, nicht doc_type).
 *
 * Mandant-Isolation ergibt sich strukturell: `docTypes` gilt schon pro Mandant,
 * `cardDocTypes` kommt aus dessen Karten (Server filtert per client_id). Ein
 * anderer Mandant (z. B. Zöscher) sieht NUR seine Doc-Typen + Unklar/Konflikt —
 * kein Leak fremder Typen.
 *
 * Filter NUR nach doc_type (data.manual_doc_type ?? data.meta.doc_type), NIE
 * nach Quelle (PDF/Excel) — `cardDocType` (lib/docTypeView.ts) ist die eine
 * doc_type-Identität, dieselbe wie serverseitig (docTypeExpr in routes/cards.ts).
 */

import { cardDocType } from "./docTypeView";

/** Ein Kanban-Filter-Tab. `id` ist zugleich der Server-Filter-Wert:
 *  "all"/"unknown"/"conflict" (Sonder-Filter) oder eine doc_type-id. */
export interface FilterTab {
  id: string;
  /** Basis-Label (ohne "(inaktiv)"-Suffix — das ergänzt die View anhand von
   *  `inactive`). Für Mandant-Doc-Typen das Mandant-Label; für card-only
   *  doc_types der kapitalisierte Slug. */
  label: string;
  /** Kategorie — steuert Zählung + Sonder-Rendering (SpecialLane). */
  kind: "all" | "unknown" | "conflict" | "doc_type";
  /** true ⇔ doc_type steht NICHT mehr in mandant_doc_types, klebt aber noch auf
   *  Karten (historischer Tab, "(inaktiv)"). Nie true für Sonder-Tabs. */
  inactive: boolean;
}

const ALL_TAB: FilterTab = { id: "all", label: "Alle", kind: "all", inactive: false };
const UNKNOWN_TAB: FilterTab = { id: "unknown", label: "Unklar", kind: "unknown", inactive: false };
const CONFLICT_TAB: FilterTab = { id: "conflict", label: "Konflikt", kind: "conflict", inactive: false };

/** #523-Rework (Thomas 23.07.): reservierter Bucket für Karten OHNE doc_type-
 *  Identität (Alt-/Vor-Split-Karten). Server zählt sie als 'ohne_typ' und
 *  filtert sie per IS-NULL — dieser Tab macht sie sichtbar statt sie unter
 *  jedem Typ-Reiter verschwinden zu lassen. Erscheint nur, wenn solche Karten
 *  existieren (n > 0 im Server-Aggregat). */
export const OHNE_TYP_ID = "ohne_typ";
const OHNE_TYP_TAB: FilterTab = { id: OHNE_TYP_ID, label: "Ohne Typ", kind: "doc_type", inactive: false };

/** Reservierte Filter-ids — kein doc_type-Tab darf diese überschreiben. */
const SPECIAL_IDS: ReadonlySet<string> = new Set(["all", "unknown", "conflict", OHNE_TYP_ID]);

/** Erster Buchstabe groß — nur für card-only doc_types ohne Mandant-Label. */
function prettify(id: string): string {
  return id.length > 0 ? id[0]!.toUpperCase() + id.slice(1) : id;
}

/**
 * Leitet die Filter-Tabs ab (siehe Modul-Doc). Deterministisch + pur.
 *
 * @param docTypes     Mandant-Doc-Typen (aus api.uploads.docTypes), in
 *                     sort_order. Nur `id`/`label` werden gebraucht.
 * @param cardDocTypes distinct doc_types der aktiven Karten (Server-Aggregat
 *                     `by_doc_type`-Keys, ODER via distinctCardDocTypes()).
 */
export function deriveFilterTabs(
  docTypes: ReadonlyArray<{ id: string; label: string }>,
  cardDocTypes: Iterable<string>,
): FilterTab[] {
  const tabs: FilterTab[] = [ALL_TAB];
  const seen = new Set<string>();
  const cardSet = new Set(cardDocTypes);

  // (a) Mandant-Doc-Typen in ihrer Reihenfolge — die aktiven Filter.
  for (const dt of docTypes) {
    if (!dt.id || SPECIAL_IDS.has(dt.id) || seen.has(dt.id)) continue;
    seen.add(dt.id);
    tabs.push({ id: dt.id, label: dt.label || prettify(dt.id), kind: "doc_type", inactive: false });
  }

  // (b) doc_types, die nur noch auf Karten vorkommen → historischer Tab
  //     "(inaktiv)". Alphabetisch für stabile UI/Tests.
  const leftover = [...cardSet]
    .filter((dt) => dt && !SPECIAL_IDS.has(dt) && !seen.has(dt))
    .sort();
  for (const dt of leftover) {
    if (seen.has(dt)) continue;
    seen.add(dt);
    tabs.push({ id: dt, label: prettify(dt), kind: "doc_type", inactive: true });
  }

  // (c) #523-Rework: existieren Karten OHNE doc_type (Server-Bucket 'ohne_typ'),
  //     bekommen sie einen eigenen sichtbaren Reiter — nichts darf verschwinden.
  if (cardSet.has(OHNE_TYP_ID)) tabs.push(OHNE_TYP_TAB);

  // Sonder-Filter zuletzt.
  tabs.push(UNKNOWN_TAB, CONFLICT_TAB);
  return tabs;
}

/** distinct doc_types einer Karten-Liste (Client-seitiger Fallback/Ergänzung
 *  zum Server-`by_doc_type`). Nutzt dieselbe doc_type-Identität wie überall. */
export function distinctCardDocTypes(cards: ReadonlyArray<{ data?: unknown }>): string[] {
  const set = new Set<string>();
  for (const c of cards) {
    const dt = cardDocType(c.data);
    // #523-Rework: typlose Karten zählen als 'ohne_typ' — Parität zum
    // Server-Aggregat, damit auch der Client-Fallback den Reiter ableitet.
    set.add(dt || OHNE_TYP_ID);
  }
  return [...set];
}

/**
 * Client-seitiges Prädikat, das die SERVER-Filter-Semantik (routes/cards.ts)
 * spiegelt — die eine Wahrheit, welche Karte in welchen Filter gehört:
 *   - "all"      → jede Karte
 *   - "unknown"  → kind=unknown
 *   - "conflict" → state=pending_conflict
 *   - <docType>  → EXAKTER Match der doc_type-Identität (nie nach Quelle!)
 *
 * Garantiert insbesondere, dass der "Zulagenblatt"-Filter NUR zulagenblatt-
 * Karten zeigt (DoD 2) — ein diaeten/fahrtenbuch/legacy-Card matcht nicht.
 */
export function cardMatchesFilter(
  card: { kind?: string | null; state?: string | null; data?: unknown },
  filterId: string,
  /** #523-Rework2: Registry-Herleitung des Mandanten (eindeutige Route je kind
   *  → Typ) — Legacy-Karten ohne doc_type-Identität zählen unter ihrem
   *  hergeleiteten Typ statt unter „Ohne Typ" (50er-Bug, Thomas 23.07.). */
  kindDefaults?: { timesheet?: string | null; expense?: string | null },
): boolean {
  if (filterId === "all") return true;
  if (filterId === "unknown") return card.kind === "unknown";
  if (filterId === "conflict") return card.state === "pending_conflict";
  const derived = cardDocType(card.data)
    ?? (card.kind === "timesheet" ? kindDefaults?.timesheet ?? null
      : card.kind === "expense" ? kindDefaults?.expense ?? null
      : null);
  // "Ohne Typ" matcht nur, was auch NACH der Herleitung typlos bleibt
  // (Spiegel der Server-Semantik: COALESCE(docTypeExpr, Registry-Route)).
  if (filterId === OHNE_TYP_ID) return !derived;
  return derived === filterId;
}
